import { useState, type ComponentType, type ReactNode } from 'react';
import {
  AlignLeft,
  ArrowDown,
  ArrowUp,
  AtSign,
  Calendar,
  CheckSquare,
  ChevronDown,
  Hash,
  Link2,
  ListChecks,
  Lock,
  Phone,
  Plus,
  Trash2,
  Type,
  X,
} from 'lucide-react';
import type { WidgetFormField } from '@/api/types';
import { Badge, Input, Segmented, Select, Switch } from '@/components/ui';
import { cn } from '@/lib/cn';

type FieldType = WidgetFormField['type'];

const FIELD_TYPES: { value: FieldType; label: string; icon: ComponentType<{ className?: string }> }[] =
  [
    { value: 'text', label: 'Short text', icon: Type },
    { value: 'textarea', label: 'Long text', icon: AlignLeft },
    { value: 'select', label: 'Dropdown', icon: ListChecks },
    { value: 'number', label: 'Number', icon: Hash },
    { value: 'date', label: 'Date', icon: Calendar },
    { value: 'email', label: 'Email', icon: AtSign },
    { value: 'phone', label: 'Phone', icon: Phone },
    { value: 'url', label: 'Link', icon: Link2 },
    { value: 'checkbox', label: 'Checkbox', icon: CheckSquare },
  ];

const MAX_FIELDS = 20;

/** "Order ID" → "order_id" — the answer's key in the API and on the ticket. */
function fieldKey(label: string): string {
  const slug = label
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '')
    .slice(0, 40);
  return /^[a-z]/.test(slug) ? slug : `field_${slug}`.replace(/_+$/, '').slice(0, 40);
}

function uniqueKey(base: string, taken: string[]): string {
  if (!taken.includes(base)) return base;
  for (let n = 2; ; n++) {
    const candidate = `${base.slice(0, 37)}_${n}`;
    if (!taken.includes(candidate)) return candidate;
  }
}

function newField(type: FieldType, taken: string[]): WidgetFormField {
  const label = FIELD_TYPES.find((t) => t.value === type)!.label;
  return {
    id: uniqueKey(fieldKey(label), taken),
    type,
    label,
    placeholder: '',
    helpText: '',
    required: false,
    options: type === 'select' ? ['Option 1', 'Option 2'] : [],
    audience: 'everyone',
    prefill: 'none',
  };
}

/**
 * Builder for the custom questions on the widget's “Report an issue” form: add, reorder, edit and
 * remove fields. Fully controlled — the parent saves `fields` with the rest of the widget.
 */
export function FormFieldsEditor({
  fields,
  onChange,
}: {
  fields: WidgetFormField[];
  onChange: (fields: WidgetFormField[]) => void;
}) {
  const [open, setOpen] = useState<number | null>(null);
  const [adding, setAdding] = useState(false);

  const update = (i: number, patch: Partial<WidgetFormField>) => {
    const current = fields[i]!;
    const next = { ...current, ...patch };
    // The key follows the label until it has been edited by hand.
    if (patch.label !== undefined && current.id.startsWith(fieldKey(current.label))) {
      next.id = uniqueKey(
        fieldKey(patch.label || current.type),
        fields.filter((_, j) => j !== i).map((f) => f.id),
      );
    }
    if (patch.type && patch.type !== 'select') next.options = [];
    if (patch.type === 'select' && next.options.length === 0) next.options = ['Option 1', 'Option 2'];
    if (patch.type && !['text', 'email'].includes(patch.type)) next.prefill = 'none';
    onChange(fields.map((f, j) => (j === i ? next : f)));
  };

  const move = (i: number, by: -1 | 1) => {
    const j = i + by;
    if (j < 0 || j >= fields.length) return;
    const next = [...fields];
    [next[i], next[j]] = [next[j]!, next[i]!];
    onChange(next);
    setOpen((o) => (o === i ? j : o === j ? i : o));
  };

  const remove = (i: number) => {
    onChange(fields.filter((_, j) => j !== i));
    setOpen((o) => (o === i ? null : o !== null && o > i ? o - 1 : o));
  };

  const add = (type: FieldType) => {
    onChange([
      ...fields,
      newField(
        type,
        fields.map((f) => f.id),
      ),
    ]);
    setOpen(fields.length);
    setAdding(false);
  };

  return (
    <div className="space-y-2">
      {fields.length === 0 && !adding && (
        <p className="rounded-xl border border-dashed border-slate-200 px-4 py-5 text-center text-sm text-slate-500">
          No custom questions yet. The form asks for a title, a category and a description.
        </p>
      )}

      {fields.map((f, i) => {
        const meta = FIELD_TYPES.find((t) => t.value === f.type) ?? FIELD_TYPES[0]!;
        const Icon = meta.icon;
        const expanded = open === i;
        return (
          <div
            key={i}
            className={cn(
              'rounded-xl border bg-white transition-shadow',
              expanded ? 'border-brand-200 shadow-card' : 'border-slate-200/80',
            )}
          >
            <div className="flex items-center gap-2 px-3 py-2.5">
              <button
                type="button"
                aria-expanded={expanded}
                onClick={() => setOpen(expanded ? null : i)}
                className="flex min-w-0 flex-1 items-center gap-2.5 text-left"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500">
                  <Icon className="h-4 w-4" />
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium text-slate-800">
                    {f.label || 'Untitled question'}
                    {f.required && <span className="text-accent-500 ml-0.5">*</span>}
                  </span>
                  <span className="block truncate font-mono text-[11px] text-slate-400">
                    {meta.label} · {f.id}
                  </span>
                </span>
                {f.audience === 'signed_in' && (
                  <Badge>
                    <Lock className="h-3 w-3" /> Signed-in only
                  </Badge>
                )}
                <ChevronDown
                  className={cn(
                    'h-4 w-4 shrink-0 text-slate-400 transition-transform',
                    expanded && 'rotate-180',
                  )}
                />
              </button>
              <span className="flex shrink-0 items-center">
                <IconButton label={`Move ${f.label} up`} disabled={i === 0} onClick={() => move(i, -1)}>
                  <ArrowUp className="h-3.5 w-3.5" />
                </IconButton>
                <IconButton
                  label={`Move ${f.label} down`}
                  disabled={i === fields.length - 1}
                  onClick={() => move(i, 1)}
                >
                  <ArrowDown className="h-3.5 w-3.5" />
                </IconButton>
                <IconButton label={`Remove ${f.label}`} danger onClick={() => remove(i)}>
                  <Trash2 className="h-3.5 w-3.5" />
                </IconButton>
              </span>
            </div>

            {expanded && <FieldSettings field={f} onChange={(patch) => update(i, patch)} />}
          </div>
        );
      })}

      {adding ? (
        <div className="rounded-xl border border-slate-200/80 bg-slate-50/60 p-3">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-xs font-semibold tracking-wide text-slate-500 uppercase">
              Choose a field type
            </p>
            <IconButton label="Cancel" onClick={() => setAdding(false)}>
              <X className="h-3.5 w-3.5" />
            </IconButton>
          </div>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
            {FIELD_TYPES.map(({ value, label, icon: Icon }) => (
              <button
                key={value}
                type="button"
                onClick={() => add(value)}
                className="hover:border-brand-300 hover:text-brand-700 flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 transition"
              >
                <Icon className="h-4 w-4 text-slate-400" /> {label}
              </button>
            ))}
          </div>
        </div>
      ) : (
        <button
          type="button"
          disabled={fields.length >= MAX_FIELDS}
          onClick={() => setAdding(true)}
          className="hover:border-brand-300 hover:text-brand-700 flex w-full items-center justify-center gap-1.5 rounded-xl border border-dashed border-slate-300 px-3 py-2.5 text-sm font-medium text-slate-600 transition disabled:cursor-not-allowed disabled:opacity-50"
        >
          <Plus className="h-4 w-4" />
          {fields.length >= MAX_FIELDS ? `Limit of ${MAX_FIELDS} questions reached` : 'Add a question'}
        </button>
      )}
    </div>
  );
}

function FieldSettings({
  field: f,
  onChange,
}: {
  field: WidgetFormField;
  onChange: (patch: Partial<WidgetFormField>) => void;
}) {
  const canPrefill = f.type === 'text' || f.type === 'email';
  return (
    <div className="space-y-4 border-t border-slate-100 px-3 py-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <Input
          label="Question"
          value={f.label}
          maxLength={80}
          onChange={(e) => onChange({ label: e.target.value })}
        />
        <Select
          label="Field type"
          value={f.type}
          onChange={(e) => onChange({ type: e.target.value as FieldType })}
        >
          {FIELD_TYPES.map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </Select>
      </div>

      <Input
        label="Answer key"
        className="font-mono text-[13px]"
        value={f.id}
        maxLength={40}
        hint="How the answer is labelled in the API and on the ticket. Lowercase letters, digits and _."
        onChange={(e) =>
          onChange({
            id: e.target.value
              .toLowerCase()
              .replace(/[^a-z0-9_]/g, '_')
              .slice(0, 40),
          })
        }
      />

      {f.type !== 'checkbox' && (
        <Input
          label="Placeholder"
          value={f.placeholder}
          maxLength={120}
          placeholder={f.type === 'select' ? 'Choose one…' : 'e.g. KOL-12345'}
          onChange={(e) => onChange({ placeholder: e.target.value })}
        />
      )}

      <Input
        label="Help text"
        value={f.helpText}
        maxLength={200}
        placeholder="Shown under the field"
        onChange={(e) => onChange({ helpText: e.target.value })}
      />

      {f.type === 'select' && (
        <div className="space-y-1.5">
          <p className="text-sm font-medium text-slate-700">Choices</p>
          <OptionsInput values={f.options} onChange={(options) => onChange({ options })} />
        </div>
      )}

      <div className="divide-y divide-slate-100 rounded-xl border border-slate-200/80">
        <Switch
          className="p-3"
          checked={f.required}
          onChange={(required) => onChange({ required })}
          label="Required"
          description={
            f.type === 'checkbox'
              ? 'The box must be ticked to submit, e.g. a confirmation.'
              : 'The form can’t be sent until this is answered.'
          }
        />
        <div className="flex flex-wrap items-center justify-between gap-3 p-3">
          <div>
            <p className="text-sm font-medium text-slate-800">Who sees it</p>
            <p className="text-xs text-slate-500">
              Signed-in only fields are hidden from anonymous visitors.
            </p>
          </div>
          <Segmented
            label="Who sees it"
            size="sm"
            value={f.audience}
            onChange={(audience) =>
              onChange({ audience, ...(audience === 'everyone' ? { prefill: 'none' } : {}) })
            }
            options={[
              { value: 'everyone', label: 'Everyone' },
              { value: 'signed_in', label: 'Signed-in users' },
            ]}
          />
        </div>
        {canPrefill && f.audience === 'signed_in' && (
          <div className="flex flex-wrap items-center justify-between gap-3 p-3">
            <div>
              <p className="text-sm font-medium text-slate-800">Prefill from their account</p>
              <p className="text-xs text-slate-500">They can still change it before sending.</p>
            </div>
            <Select
              aria-label="Prefill from their account"
              wrapperClassName="w-40"
              value={f.prefill}
              onChange={(e) => onChange({ prefill: e.target.value as WidgetFormField['prefill'] })}
            >
              <option value="none">Don’t prefill</option>
              <option value="name">Their name</option>
              <option value="email">Their email</option>
            </Select>
          </div>
        )}
      </div>
    </div>
  );
}

/** Dropdown choices: Enter adds one, × removes it. */
function OptionsInput({ values, onChange }: { values: string[]; onChange: (v: string[]) => void }) {
  const [text, setText] = useState('');
  const add = () => {
    const v = text.trim().slice(0, 60);
    if (v && !values.some((c) => c.toLowerCase() === v.toLowerCase())) onChange([...values, v].slice(0, 30));
    setText('');
  };
  return (
    <div className="focus-within:border-brand-300 focus-within:ring-brand-500/10 flex flex-wrap items-center gap-1.5 rounded-xl border border-slate-200 bg-white p-2 focus-within:ring-4">
      {values.map((c) => (
        <span
          key={c}
          className="bg-brand-50 text-brand-700 ring-brand-100 inline-flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium ring-1"
        >
          {c}
          <button
            type="button"
            aria-label={`Remove ${c}`}
            onClick={() => onChange(values.filter((x) => x !== c))}
            className="hover:text-brand-900"
          >
            <X className="h-3 w-3" />
          </button>
        </span>
      ))}
      <input
        aria-label="Add a choice"
        value={text}
        placeholder={values.length ? 'Add another' : 'Type a choice and press Enter'}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === 'Enter') {
            e.preventDefault();
            add();
          } else if (e.key === 'Backspace' && !text && values.length) {
            onChange(values.slice(0, -1));
          }
        }}
        onBlur={add}
        className="min-w-[10rem] flex-1 bg-transparent px-1 py-1 text-sm outline-none placeholder:text-slate-400"
      />
    </div>
  );
}

function IconButton({
  label,
  onClick,
  disabled,
  danger,
  children,
}: {
  label: string;
  onClick: () => void;
  disabled?: boolean;
  danger?: boolean;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      title={label}
      disabled={disabled}
      onClick={onClick}
      className={cn(
        'flex h-7 w-7 items-center justify-center rounded-md text-slate-400 transition disabled:opacity-30',
        danger ? 'hover:bg-red-50 hover:text-red-600' : 'hover:bg-slate-100 hover:text-slate-700',
      )}
    >
      {children}
    </button>
  );
}
